import { memo, useMemo, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

import isBetween from 'dayjs/plugin/isBetween.js';
import { useDisclosure } from '@heroui/react';
import NavList from './comps/NavList/index.jsx';
import { useInViewport, useMemoizedFn } from 'ahooks';
import Nav from './comps/Nav/index.jsx';
import Header from './comps/Header/index.jsx';
import isEqual from 'react-fast-compare';
import Container from './comps/Container/index.jsx';
import AddEvent from '@/components/AddEvent/index.jsx';
import { type IRenderListContext, renderList } from './utils/renderList.jsx';
import useEditCustom from './comps/Custom/useEditCustom.jsx';
import { useParams } from 'react-router-dom';
import type { getDataService } from './service.js';
import { planDetailEvent } from './utils/event.js';
import ExpenseDetail from '../ExpenseDetail/index.jsx';
import usePlanDetailScroll from './hooks/usePlanDetailScroll.js';
import usePlanDetailData from './hooks/usePlanDetailData.js';
import usePlanList from './hooks/usePlanList.js';

dayjs.extend(isBetween);

function setThemeColor(color: string): void {
  const metaElement = document.querySelector(
    'meta[name="theme-color"]'
  ) as HTMLMetaElement;
  if (metaElement) {
    metaElement.content = color;
  }
}

const Inner = memo(
  ({
    id,
    startTime,
    endTime,
    data,
  }: {
    id: string;
    startTime: Dayjs;
    endTime: Dayjs;
    data: Awaited<ReturnType<typeof getDataService>>;
  }) => {
    const requestRefresh = useMemoizedFn(() => {
      planDetailEvent.emit('requestRefresh');
    });

    const { date, onPlanDetailLoaded, navListProps, scrollContainerProps } =
      usePlanDetailScroll({ startTime, endTime });

    const {
      isOpen: isAddOpen,
      onOpen: onAddOpen,
      onOpenChange: onAddOpenChange,
    } = useDisclosure();

    const [isAppendAdd, setIsAppendAdd] = useState(false);

    const getList = useMemoizedFn(() => {
      return list;
    });

    const { show: showEditCustom, placeholder: editCustomPlaceholder } =
      useEditCustom({ getList });

    const onAddOpenFn = useMemoizedFn(
      (payload?: { isAppend?: boolean; item: { props: { date: string } } }) => {
        onAddOpen();
        setIsAppendAdd(!!payload?.isAppend);
        if (payload?.item.props.date) {
          setCurrentAddDate(payload.item.props.date);
        }
      }
    );

    const { list } = usePlanList({
      date,
      id,
      onLoaded: onPlanDetailLoaded,
      onAddOpen: onAddOpenFn,
      showEditCustom,
    });

    const [currentAddDate, setCurrentAddDate] = useState('');
    const renderListContext = useMemo(() => {
      const result: IRenderListContext = {
        planId: id as string,
        getList,
      };

      return result;
    }, [id]);
    const renderListFn = useMemoizedFn((item: NonNullable<typeof list>[0]) => {
      return renderList(item, renderListContext);
    });

    const headerPlaceholderRef = useRef(null);
    const [inViewport] = useInViewport(headerPlaceholderRef);

    return (
      <>
        <div
          className='min-h-screen before:fixed before:-z-10 before:bg-white before:w-full before:h-1/2  after:top-0 after:fixed after:-z-10 after:bg-inherit after:w-full after:h-1/2  '
          style={{
            backgroundColor: data?.bgColor,
          }}
        >
          <Nav onSync={requestRefresh} showExtraBtn={!inViewport} planId={id} />
          <Header
            startTime={startTime}
            endTime={endTime}
            title={data?.title}
            planId={id}
          />
          <div ref={headerPlaceholderRef} />
          <NavList {...navListProps} location={data?.location} planId={id} />
          <Container {...scrollContainerProps}>
            {list?.map(renderListFn)}
          </Container>
        </div>
        <AddEvent
          isOpen={isAddOpen}
          onOpenChange={onAddOpenChange}
          date={currentAddDate}
          onSuccess={requestRefresh}
          isAppendAdd={isAppendAdd}
          planId={id}
        />

        {editCustomPlaceholder}
        <ExpenseDetail
          onClose={() => {
            history.back();
          }}
          planId={id}
        />
      </>
    );
  },
  isEqual
);

const PlanDetail = memo(() => {
  const { id } = useParams();
  const { data, startTime, endTime } = usePlanDetailData(id, {
    onSuccess(res) {
      res?.bgColor && setThemeColor(res.bgColor);
    },
  });

  if (!data || !id) return null;
  return <Inner id={id} startTime={startTime} endTime={endTime} data={data} />;
}, isEqual);

export default PlanDetail;
